import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { ApiResponse, MenstrualHistory } from '../../../../components/interface/SubjectiveInterfaces/MenstrualHistoryInterface';
import { BASE_URL, getAxiosConfig } from '../../../../components/interface/Config';
import LoadingAnimation from '../../../../components/LoadingAnimation';


interface MenstrualHistoryFormProps {
  patientId: string
}

const MenstrualHistoryForm: React.FC<MenstrualHistoryFormProps> = ({ patientId }) => {
  const initialFormData: MenstrualHistory = {
    menarcheAge: 0,
    cyclesPerYear: 0,
    cycleLengthInDays: 0,
    flowDuration: '',
    flowType: '',
    intermenstrualBleeding: '',
    cycleIrregularity: '',
    dysmenorrhea: '',
    lmpDate: '',
    menopause: '',
    menopause_age: 0
  };

  const [formData, setFormData] = useState<MenstrualHistory>(initialFormData);
  const [recordId, setRecordId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [noData, setNoData] = useState(false);

  const fetchMenstrualHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get<ApiResponse>(
        `${BASE_URL}/resource/patientHistories`,
        {
          params: {
            slug: 'menstrual-history',
            user_id: patientId,
            limit: 10,
            page: 1,
            order_by: 'id',
            dir: 2
          },
          headers: getAxiosConfig().headers
        }
      );
      if (
        response.data &&
        response.data.data &&
        response.data.data.patient_histories &&
        response.data.data.patient_histories.length > 0
      ) {
        const latestHistory = response.data.data.patient_histories[0];
        setRecordId(latestHistory.id);
        const values = latestHistory.values;
        setFormData({
          ...formData,
          lmpDate: values.lmp || '',
          menarcheAge: parseInt(values.menarche_age, 10) || 0,
          cyclesPerYear: parseInt(values.cycle?.year, 10) || 0,
          cycleLengthInDays: parseInt(values.cycle?.days, 10) || 0,
          flowDuration: values.flow?.duration || '',
          flowType: values.flow?.type || '',
          intermenstrualBleeding: values.additional_values?.bleeding || '',
          cycleIrregularity: values.cycle?.irregular || '',
          dysmenorrhea: values.dysmenorrhea || '',
          menopause: values.menopause || '',
          menopause_age: values.menopause_age || 0
        });
        setNoData(false);
      } else {
        setNoData(true);
      }
    } catch (error) {
      console.error('Error fetching patient history:', error);
      setNoData(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (patientId) {
      fetchMenstrualHistory();
    }
  }, [patientId]);

  return (
    <div className="flex flex-col max-h-screen">
      <table className="w-full border-collapse border border-gray-300">
        <thead>
          <tr className="bg-primary text-white">
            <th className="border border-white px-4 py-2">Menstrual History</th>
            <th className="border border-white px-4 py-2">Data</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={2} className="border border-gray-300 px-4 py-2">
                <div className="flex justify-center items-center h-32">
                  <LoadingAnimation />
                </div>
              </td>
            </tr>
          ) : noData ? (
            <tr>
              <td colSpan={2} className="border border-gray-300 px-4 py-2 text-center">
                No data found
              </td>
            </tr>
          ) : (
            <>
              <tr>
                <td className="border border-gray-300 px-4 py-2">Menarche Age</td>
                <td className="border border-gray-300 px-4 py-2">
                  {formData.menarcheAge === 0 ? '' : formData.menarcheAge}
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2">LMP Date</td>
                <td className="border border-gray-300 px-4 py-2">{formData.lmpDate}</td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2">Flow Duration</td>
                <td className="border border-gray-300 px-4 py-2">{formData.flowDuration}</td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2">Flow Type</td>
                <td className="border border-gray-300 px-4 py-2">{formData.flowType}</td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2">Cycle Length in Days</td>
                <td className="border border-gray-300 px-4 py-2">
                  {formData.cycleLengthInDays === 0 ? '' : formData.cycleLengthInDays}
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2">Cycles Per Year</td>
                <td className="border border-gray-300 px-4 py-2">
                  {formData.cyclesPerYear === 0 ? '' : formData.cyclesPerYear}
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2">Intermenstrual Bleeding</td>
                <td className="border border-gray-300 px-4 py-2">
                  {formData.intermenstrualBleeding}
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2">Cycle Irregularity</td>
                <td className="border border-gray-300 px-4 py-2">{formData.cycleIrregularity}</td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2">Dysmenorrhea</td>
                <td className="border border-gray-300 px-4 py-2">{formData.dysmenorrhea}</td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2">Menopause</td>
                <td className="border border-gray-300 px-4 py-2">{formData.menopause}</td>
              </tr>
              {formData.menopause === 'yes' && (
                <tr>
                  <td className="border border-gray-300 px-4 py-2">Menopause Age</td>
                  <td className="border border-gray-300 px-4 py-2">{formData.menopause_age}</td>
                </tr>
              )}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default MenstrualHistoryForm;
